import { Slot, useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import { I18nextProvider } from 'react-i18next';

import { NotFoundScreen } from '@todo/web-ui/components/not-found-screen';
import { getInstanceForLocale } from '@/lib/i18n';
import { prefetchLists } from '@/lib/prefetch';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, isSupportedLocale } from '@todo/core/i18n/locales';
import { ogLocale, ogLocaleAlternates, organizationJsonLd } from '@todo/core/seo';

// Owns the locale for everything under app/[lang]/** — the locale-scoped
// i18next instance (never the mutable global one: the export renders /en, /es
// and /ru in one process), <html lang>, og:locale and the Organization entity.
export default function LangLayout() {
  const { lang } = useLocalSearchParams<{ lang: string }>();
  const supported = isSupportedLocale(lang);
  const locale = supported ? lang : DEFAULT_LOCALE;
  const instance = getInstanceForLocale(locale);

  return (
    <I18nextProvider i18n={instance}>
      {/* `key`: remount on a client-side language switch, or Helmet leaves
          the previous locale's head tags (see apps/riviera's [lang] layout). */}
      <Head key={locale}>
        <html lang={locale} />
        <meta property="og:locale" content={ogLocale(locale)} />
        {ogLocaleAlternates(locale).map((alt) => (
          <meta key={alt} property="og:locale:alternate" content={alt} />
        ))}
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd(locale))}</script>
      </Head>
      {/* Any first segment that isn't a locale is a page that doesn't exist.
          This is also what the export renders for +not-found (its path
          matches [lang]), so 404.html is the "not found" page. */}
      {supported ? <Slot /> : <NotFoundScreen />}
    </I18nextProvider>
  );
}

// Async so every list is fetched before anything in this subtree renders —
// the one hook guaranteed to run, and finish, first (src/lib/prefetch.ts).
export async function generateStaticParams(): Promise<{ lang: string }[]> {
  await prefetchLists();
  return SUPPORTED_LOCALES.map((lang) => ({ lang }));
}
